import React, { useEffect } from "react";
import {
  Route,
  Routes,
  Navigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { Capacitor } from "@capacitor/core";
import { HelmetProvider } from "react-helmet-async";

// Import Mixpanel service
import mixpanelService from "./services/MixpanelService";
// Import PostHog service
import postHogService from "./services/PostHogService";

import Landing from "./landing/Landing";
import TermsOfService from "./landing/Legal/TermsOfService";
import PrivacyPolicy from "./landing/Legal/PrivacyPolicy";
import DeleteMyData from "./landing/Legal/DeleteMyData";

import ProtectedRoute from "./services/ProtectedRoute";
import OnboardingRoute from "./services/OnboardingRoute";
import AuthRedirect from "./services/AuthRedirect";
import FirebaseAuthCallback from "./services/FirebaseAuthCallback";
import { isNative } from "./services/isNative";

import Login from "./pages/Login/Login";

import SaveAccount from "./pages/Login/SaveAccount";
import DownloadApp from "./pages/Login/DownloadApp";
import DownloadApp2 from "./pages/Login/DownloadApp-2";
import DownloadVibeCheck from "./pages/DownloadVibeCheck";
import AgeRestriction from "./pages/Login/AgeRestriction";

import OnboardingStart from "./pages/Login/Onboarding/OnboardingStart";
import OnboardingIntro from "./pages/Login/Onboarding/OnboardingIntro";
import OnboardingOne from "./pages/Login/Onboarding/Onboarding-1";
import OnboardingOneTwentyFive from "./pages/Login/Onboarding/Onboarding-1.25";
import OnboardingOneFive from "./pages/Login/Onboarding/Onboarding-1.5";
import OnboardingTwo from "./pages/Login/Onboarding/Onboarding-2";
import OnboardingThree from "./pages/Login/Onboarding/Onboarding-3";
import OnboardingNotifications from "./pages/Login/Onboarding/Onboarding-Notifications";
import QuizIntro from "./pages/Login/Onboarding/QuizIntro";

import Home from "./pages/Home";
import Inbox from "./pages/Inbox";
import Profile from "./pages/Profile";
import PublicProfile from "./pages/PublicProfile";
import SettingsOverview from "./pages/SettingsOverview";
import SettingsProfile from "./pages/SettingsProfile";
import SettingsNotifications from "./pages/SettingsNotifications";
import Friends from "./pages/Friends";
import Confirmation from "./pages/Confirmation";

import LandingFD from "./pages/static-quizzes/First-Date-Quiz/LandingFD";
import ResultsFD from "./pages/static-quizzes/First-Date-Quiz/ResultsFD";
import LandingAIG from "./pages/static-quizzes/Am-I-Gay-Quiz/LandingAIG";
import ResultsAIG from "./pages/static-quizzes/Am-I-Gay-Quiz/ResultsAIG";

import LandingPA from "./pages/static-quizzes/Political-Alignment-Quiz/LandingPA";
import ResultsPA from "./pages/static-quizzes/Political-Alignment-Quiz/ResultsPA";

import LandingMB from "./pages/static-quizzes/Personality-Check-Quiz/LandingMB";
import ResultsMB from "./pages/static-quizzes/Personality-Check-Quiz/ResultsMB";

import LandingSS from "./pages/static-quizzes/Sex-Style-Quiz/LandingSexStyle";
import ResultsSS from "./pages/static-quizzes/Sex-Style-Quiz/ResultsSS";

import LandingAS from "./pages/static-quizzes/Attachment-Styles-Quiz/Landing";
import ResultsAS from "./pages/static-quizzes/Attachment-Styles-Quiz/Results";

import LandingAffection from "./pages/static-quizzes/Affection-Profile-Quiz/Landing";
import ResultsAffection from "./pages/static-quizzes/Affection-Profile-Quiz/Results";

import LandingLoveLanguages from "./pages/static-quizzes/Love-Languages-Quiz/Landing";
import ResultsLoveLanguages from "./pages/static-quizzes/Love-Languages-Quiz/Results";

import UniversalQuestionPage from "./templates/UniversalQuestionPage";
import SharedAnswersComparison from "./components/SharedAnswersComparison";
import Pro from "./pages/Pro";

// Dynamic quiz components for remote quiz configuration
import {
  DynamicQuizLanding,
  DynamicQuizQuestion,
  DynamicQuizResults,
} from "./templates/DynamicQuiz";

// Static quiz types that use legacy routes (/{quizType})
const LEGACY_QUIZ_TYPES = new Set([
  "best-date",
  "personality-check",
  "political-alignment",
  "sex-styles",
  "attachment-styles",
  "am-i-gay",
  "affection-profile",
  "love-languages",
]);

// Redirect component for shared quiz links (/s/:quizType/:username?from=xxx)
const SharedQuizRedirect = () => {
  const { quizType } = useParams();
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get("from");

  // Legacy quizzes use /{quizType}, new cloud quizzes use /quiz/{quizType}
  const isLegacyQuiz = LEGACY_QUIZ_TYPES.has(quizType);
  const basePath = isLegacyQuiz ? `/${quizType}` : `/quiz/${quizType}`;
  const targetUrl = fromParam ? `${basePath}?from=${fromParam}` : basePath;

  return <Navigate to={targetUrl} replace />;
};

export default function App() {
  useEffect(() => {
    // Initialize Mixpanel
    mixpanelService.init("fd6e377d02b3cf037ad717dfab639766");

    // Initialize PostHog
    postHogService.init("phc_mhQ1TM5KQfvQuCa3TWsQDDz68IygczquDeCO96Pec9y");

    if (Capacitor.isNativePlatform()) {
      ScreenOrientation.lock({
        orientation: "portrait",
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <Routes>
        <Route path="/" element={isNative ? <AuthRedirect /> : <Landing />} />
        <Route path="/firebaseauth/link" element={<FirebaseAuthCallback />} />
        <Route path="/login" element={<Login />} />
        <Route path="/save-account" element={<SaveAccount />} />
        <Route path="/confirmation" element={<Confirmation />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/delete-my-data" element={<DeleteMyData />} />
        <Route path="/download-app" element={<DownloadApp />} />
        <Route path="/download" element={<DownloadApp2 />} />
        <Route path="/download-vibe-check" element={<DownloadVibeCheck />} />
        <Route path="/age-restriction" element={<AgeRestriction />} />

        <Route
          path="/onboarding-start"
          element={
            <OnboardingRoute>
              <OnboardingStart />
            </OnboardingRoute>
          }
        />
        <Route
          path="/onboarding-intro"
          element={
            <OnboardingRoute>
              <OnboardingIntro />
            </OnboardingRoute>
          }
        />
        <Route
          path="/onboarding-one"
          element={
            <OnboardingRoute>
              <OnboardingOne />
            </OnboardingRoute>
          }
        />
        <Route
          path="/onboarding-one-twenty-five"
          element={
            <OnboardingRoute>
              <OnboardingOneTwentyFive />
            </OnboardingRoute>
          }
        />
        <Route
          path="/onboarding-one-five"
          element={
            <OnboardingRoute>
              <OnboardingOneFive />
            </OnboardingRoute>
          }
        />
        <Route
          path="/onboarding-two"
          element={
            <OnboardingRoute>
              <OnboardingTwo />
            </OnboardingRoute>
          }
        />
        <Route
          path="/onboarding-three"
          element={
            <OnboardingRoute>
              <OnboardingThree />
            </OnboardingRoute>
          }
        />
        <Route
          path="/quiz-intro"
          element={
            <OnboardingRoute>
              <QuizIntro />
            </OnboardingRoute>
          }
        />
        <Route
          path="/onboarding-notifications"
          element={
            <OnboardingRoute>
              <OnboardingNotifications />
            </OnboardingRoute>
          }
        />

        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inbox"
          element={
            <ProtectedRoute>
              <Inbox />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/friends"
          element={
            <ProtectedRoute>
              <Friends />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings-profile"
          element={
            <ProtectedRoute>
              <SettingsProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings-notifications"
          element={
            <ProtectedRoute>
              <SettingsNotifications />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings-overview"
          element={
            <ProtectedRoute>
              <SettingsOverview />
            </ProtectedRoute>
          }
        />

        <Route
          path="/pro"
          element={
            <ProtectedRoute>
              <Pro />
            </ProtectedRoute>
          }
        />

        <Route
          path="/shared-answers/:sharedId"
          element={
            <ProtectedRoute>
              <SharedAnswersComparison />
            </ProtectedRoute>
          }
        />

        <Route path="/political-alignment" element={<LandingPA />} />
        <Route path="/political-alignment/results" element={<ResultsPA />} />
        <Route
          path="/political-alignment/question/:questionId"
          element={<UniversalQuestionPage quizType="political-alignment" />}
        />

        <Route path="/best-date" element={<LandingFD />} />
        <Route path="/best-date/results" element={<ResultsFD />} />
        <Route
          path="/best-date/question/:questionId"
          element={<UniversalQuestionPage quizType="best-date" />}
        />

        <Route path="/personality-check" element={<LandingMB />} />
        <Route path="/personality-check/results" element={<ResultsMB />} />
        <Route
          path="/personality-check/question/:questionId"
          element={<UniversalQuestionPage quizType="personality-check" />}
        />

        <Route path="/sex-styles" element={<LandingSS />} />
        <Route path="/sex-styles/results" element={<ResultsSS />} />
        <Route
          path="/sex-styles/question/:questionId"
          element={<UniversalQuestionPage quizType="sex-styles" />}
        />

        <Route path="/attachment-styles" element={<LandingAS />} />
        <Route path="/attachment-styles/results" element={<ResultsAS />} />
        <Route
          path="/attachment-styles/question/:questionId"
          element={<UniversalQuestionPage quizType="attachment-styles" />}
        />

        <Route path="/affection-profile" element={<LandingAffection />} />
        <Route
          path="/affection-profile/results"
          element={<ResultsAffection />}
        />
        <Route
          path="/affection-profile/question/:questionId"
          element={<UniversalQuestionPage quizType="affection-profile" />}
        />

        <Route path="/am-i-gay" element={<LandingAIG />} />
        <Route path="/am-i-gay/results" element={<ResultsAIG />} />
        <Route
          path="/am-i-gay/question/:questionId"
          element={<UniversalQuestionPage quizType="am-i-gay" />}
        />

        <Route path="/love-languages" element={<LandingLoveLanguages />} />
        <Route
          path="/love-languages/results"
          element={<ResultsLoveLanguages />}
        />
        <Route
          path="/love-languages/question/:questionId"
          element={<UniversalQuestionPage quizType="love-languages" />}
        />

        {/*
          Shared Quiz Redirect Route
          Handles URLs like /s/love-languages/Username?from=quizId
          Redirects to /:quizType?from=quizId (the quiz landing page)
        */}
        <Route path="/s/:quizType/:username" element={<SharedQuizRedirect />} />

        {/*
          Dynamic Quiz Routes (for remotely configured quizzes)

          These routes handle quizzes that are configured in Firestore
          but don't have static routes defined above. New quiz types
          added via Firebase Console will automatically work with these routes.

          Note: Static routes above take precedence, so existing quizzes
          will continue to use their optimized static implementations.
        */}
        <Route path="/quiz/:quizType" element={<DynamicQuizLanding />} />
        <Route
          path="/quiz/:quizType/question/:questionId"
          element={<DynamicQuizQuestion />}
        />
        <Route
          path="/quiz/:quizType/results"
          element={<DynamicQuizResults />}
        />

        {/* Public Profile - must be last as catch-all */}
        <Route path="/:username" element={<PublicProfile />} />
      </Routes>
    </HelmetProvider>
  );
}
